/**
 * P&L Statement Report (UI-6 v3 layout, BE-10 data)
 *
 * Financial statement from the ledger: each section lists its accounts with a total, compared with the same days one
 * period earlier. Accrual / Cash view (the TC's official basis first), corporate tax estimate, below-the-line
 * pass-throughs and the server's notes. Print-optimized styles.
 * Route: /reports/profit-loss
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Chip, Alert, Skeleton, Tooltip, useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { PeriodSelector, CurrencyText, ReportExportMenu } from '../../components/accounting';
import { formatDate } from '../../utils/accounting/formatters';
import { getPeriodDates, getComparisonDates, getComparisonLabel, toYmd } from '../../utils/accounting/periodUtils';
import { profitLossService } from '../../services/accounting/profitLoss.service';
import { serverMessage } from '../../utils/apiConvert';

/* ── helpers ──────────────────────────────────────────────── */

const BASIS_LABEL = { ACCRUAL: 'Accrual', CASH: 'Cash' };

// Percent change from the earlier amount; null when there is nothing to compare against.
const pct = (current, previous) => {
  const c = Number(current || 0);
  const p = Number(previous || 0);
  if (p === 0) return null;
  return ((c - p) / Math.abs(p)) * 100;
};

const percentText = (v) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(1)}%`);

const ChangeIndicator = ({ change, invert = false }) => {
  if (change === null || change === undefined) {
    return <Typography component="span" sx={{ fontSize: fs.xs, color: 'text.secondary' }}>—</Typography>;
  }
  const value = Number(change);
  if (Math.round(value) === 0) return <Typography component="span" sx={{ fontSize: fs.xs, color: 'text.secondary' }}>0%</Typography>;
  const up = value > 0;
  const good = invert ? !up : up;
  return (
    <Typography component="span" sx={{ fontSize: fs.xs, color: good ? 'success.main' : 'error.main', fontWeight: fw.medium }}>
      {up ? '▲' : '▼'}{Math.abs(value).toFixed(0)}%
    </Typography>
  );
};

/* ── row components ───────────────────────────────────────── */

const Amount = ({ value, text, negative, bold, muted, size = 'sm' }) => (
  text !== undefined
    ? <Typography component="span" sx={{ fontSize: fs[size], fontWeight: bold ? fw.bold : fw.normal, color: muted ? 'text.secondary' : 'text.primary' }}>{text}</Typography>
    : <CurrencyText amount={value} negative={negative} bold={bold} size={size} withPrefix={false} sx={muted ? { color: 'text.secondary' } : {}} />
);

// variant: 'line' (an account), 'total' (a section total on a rule), 'result' (a profit band), 'net' (net profit)
const StatementRow = ({
  label, current, comparison, currentText, comparisonText, change, compare, indent = 0, bold = false,
  showChange = true, negative = false, invert = false, note, variant = 'line',
}) => {
  const theme = useTheme();
  const isTotal = variant === 'total';
  const isNet = variant === 'net';
  const strong = bold || isTotal || variant === 'result' || isNet;
  return (
    <>
      <Box
        sx={{
          display: 'flex', alignItems: 'center', py: isNet ? 0.9 : 0.5, px: 1.5,
          ...(isTotal && { borderTop: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.text.primary, 0.03), mt: 0.25 }),
          ...(variant === 'line' && { '&:hover': { bgcolor: theme.palette.action.hover } }),
          '@media print': { py: 0.2 },
        }}
      >
        <Typography sx={{
          flex: 1, pl: indent * 2, fontSize: isNet ? fs.md : fs.sm, fontWeight: strong ? fw.bold : fw.normal,
          color: variant === 'line' && !bold ? 'text.secondary' : 'text.primary',
        }}>
          {label}
        </Typography>
        <Box sx={{ width: 120, textAlign: 'right' }}>
          <Amount value={current} text={currentText} negative={negative} bold={strong} size={isNet ? 'md' : 'sm'} />
        </Box>
        {compare && (
          <Box sx={{ width: 120, textAlign: 'right' }}>
            <Amount value={comparison} text={comparisonText} negative={negative} muted />
          </Box>
        )}
        {compare && (
          <Box sx={{ width: 70, textAlign: 'right' }}>
            {showChange && <ChangeIndicator change={change} invert={invert} />}
          </Box>
        )}
      </Box>
      {note && (
        <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', pl: indent * 2 + 1, pr: 1, mt: -0.25, mb: 0.25 }}>{note}</Typography>
      )}
    </>
  );
};

const SectionHeader = ({ label }) => {
  const theme = useTheme();
  return (
    <Box sx={{
      mt: 1.5, py: 0.6, px: 1.5, borderLeft: 3, borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.1),
      '@media print': { bgcolor: 'transparent' },
    }}>
      <Typography sx={{ fontSize: fs.xs, fontWeight: fw.bold, color: 'primary.main', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        {label}
      </Typography>
    </Box>
  );
};

// A profit band (Gross / Operating / Net): tinted in the brand colour; Net Profit tinted green or red by its sign.
const ResultBand = ({ children, tone }) => {
  const theme = useTheme();
  const color = tone === 'profit' ? theme.palette.success.main : tone === 'loss' ? theme.palette.error.main : theme.palette.primary.main;
  return (
    <Box sx={{ mt: 1.5, bgcolor: alpha(color, tone ? 0.14 : 0.07), borderTop: 1, borderBottom: 1, borderColor: alpha(color, 0.35) }}>
      {children}
    </Box>
  );
};

const ColumnHeaders = ({ comparisonLabel, comparisonDates }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', py: 0.75, px: 1.5, borderBottom: 1, borderColor: 'divider' }}>
    <Typography sx={{ flex: 1, fontSize: fs.xs, color: 'text.secondary', fontWeight: fw.medium }}>&nbsp;</Typography>
    <Typography sx={{ width: 120, textAlign: 'right', fontSize: fs.xs, color: 'text.secondary', fontWeight: fw.medium }}>
      Current
    </Typography>
    <Tooltip title={comparisonDates || ''}>
      <Typography sx={{ width: 120, textAlign: 'right', fontSize: fs.xs, color: 'text.secondary', fontWeight: fw.medium }}>
        {comparisonLabel || 'Previous'}
      </Typography>
    </Tooltip>
    <Typography sx={{ width: 70, textAlign: 'right', fontSize: fs.xs, color: 'text.secondary', fontWeight: fw.medium }}>
      Change
    </Typography>
  </Box>
);

// One row per account in either range, current order first; a line's change is worked out from the two amounts.
const AccountLines = ({ section, compSection, negative, invert }) => {
  const lines = section?.lines || [];
  const compLines = compSection?.lines || [];
  const accounts = [...lines.map((l) => l.account), ...compLines.map((l) => l.account).filter((a) => !lines.some((l) => l.account === a))];
  return accounts.map((account) => {
    const line = lines.find((l) => l.account === account);
    const compLine = compLines.find((l) => l.account === account);
    return (
      <StatementRow key={account} label={(line || compLine).name} current={line?.amount || 0} comparison={compLine?.amount || 0}
        change={pct(line?.amount, compLine?.amount)} compare={Boolean(compSection)} indent={1} negative={negative} invert={invert} />
    );
  });
};

/* ── loading skeleton ─────────────────────────────────────── */

const StatementSkeleton = () => (
  <Box sx={{ px: 1 }}>
    {[...Array(12)].map((_, i) => (
      <Box key={i} sx={{ display: 'flex', py: 0.5, gap: 2 }}>
        <Skeleton width={`${30 + (i % 4) * 5}%`} height={20} />
        <Box sx={{ flex: 1 }} />
        <Skeleton width={80} height={20} />
        <Skeleton width={80} height={20} />
        <Skeleton width={50} height={20} />
      </Box>
    ))}
  </Box>
);

/* ── main component ───────────────────────────────────────── */

const ProfitLoss = () => {
  const theme = useTheme();
  const [period, setPeriod] = useState('THIS_MONTH');
  const [basis, setBasis] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { from, to } = getPeriodDates(period);
      const comp = getComparisonDates(period);
      setData(await profitLossService.get({
        from: toYmd(from), to: toYmd(to), basis, compareFrom: toYmd(comp.from), compareTo: toYmd(comp.to),
      }));
    } catch (err) {
      setError(serverMessage(err, 'Failed to load P&L statement'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [period, basis]);

  useEffect(() => { loadData(); }, [loadData]);

  const d = data;
  const c = data?.comparison;
  const pc = c?.percentChange || {};
  const compare = Boolean(c);
  const compLabel = getComparisonLabel(period);
  const compDates = c ? `${formatDate(c.period.from)} – ${formatDate(c.period.to)}` : '';
  const shownBasis = d?.basis || basis;
  const exportRange = getPeriodDates(period);
  const exportParams = { from: toYmd(exportRange.from), to: toYmd(exportRange.to), basis: shownBasis };

  const taxRow = (tax) => (tax?.amount === null || tax?.amount === undefined ? { text: 'n/a' } : { value: tax.amount });
  const curTax = taxRow(d?.corporateTax);
  const compTax = taxRow(c?.corporateTax);

  return (
    <Box sx={{ p: 2, width: 0, minWidth: '100%', boxSizing: 'border-box', '@media print': { p: 0 } }}>
      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontSize: fs.md, fontWeight: fw.semibold, mb: 0.25 }}>
            Profit & Loss
          </Typography>
          {shownBasis && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', '@media print': { display: 'none' } }}>
              {['ACCRUAL', 'CASH'].map((b) => (
                <Chip
                  key={b}
                  label={`${BASIS_LABEL[b]} basis`}
                  size="small"
                  color={shownBasis === b ? 'primary' : 'default'}
                  variant={shownBasis === b ? 'filled' : 'outlined'}
                  onClick={shownBasis === b || loading ? undefined : () => setBasis(b)}
                  sx={{
                    fontSize: fs.xs, height: 22,
                    ...(shownBasis === b
                      ? { bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText }
                      : { bgcolor: theme.palette.action.selected, borderColor: 'transparent' }),
                  }}
                />
              ))}
              {d && d.basis !== d.officialBasis && (
                <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', ml: 0.5 }}>
                  Official basis: {BASIS_LABEL[d.officialBasis] || d.officialBasis}
                </Typography>
              )}
            </Box>
          )}
          {shownBasis && (
            <Typography sx={{ display: 'none', fontSize: fs.xs, '@media print': { display: 'block' } }}>
              {BASIS_LABEL[shownBasis]} basis
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', '@media print': { display: 'none' } }}>
          <PeriodSelector value={period} onChange={setPeriod} />
          <ReportExportMenu reportType="profit-loss" params={exportParams} onError={setError} />
        </Box>
      </Box>

      {/* Statement body */}
      <Box
        sx={{
          bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider', overflowX: 'auto',
          '@media print': { border: 'none', boxShadow: 'none' },
        }}
      >
        <Box sx={{ minWidth: 520 }}>
          {loading ? (
            <StatementSkeleton />
          ) : d ? (
            <>
              <ColumnHeaders comparisonLabel={compLabel} comparisonDates={compDates} />

              {/* REVENUE */}
              <SectionHeader label="Revenue" />
              <AccountLines section={d.revenue} compSection={c?.revenue} />
              <StatementRow label="Total Revenue" current={d.revenue.total} comparison={c?.revenue?.total} change={pc.revenue}
                compare={compare} variant="total" />

              {/* COST OF SERVICES */}
              <SectionHeader label="Cost of Services" />
              <AccountLines section={d.costOfServices} compSection={c?.costOfServices} negative invert />
              <StatementRow label="Total Cost of Services" current={d.costOfServices.total} comparison={c?.costOfServices?.total}
                change={pc.costOfServices} compare={compare} negative invert variant="total" />

              {/* GROSS PROFIT */}
              <ResultBand>
                <StatementRow label="GROSS PROFIT" current={d.grossProfit} comparison={c?.grossProfit} change={pc.grossProfit} compare={compare} variant="result" />
                <StatementRow label="Gross Margin" currentText={percentText(d.grossMarginPercent)}
                  comparisonText={c ? percentText(c.grossMarginPercent) : undefined} compare={compare} indent={1} showChange={false} variant="info" />
              </ResultBand>

              {/* OPERATING EXPENSES */}
              <SectionHeader label="Operating Expenses" />
              <AccountLines section={d.operatingExpenses} compSection={c?.operatingExpenses} negative invert />
              <StatementRow label="Total Operating Expenses" current={d.operatingExpenses.total} comparison={c?.operatingExpenses?.total}
                change={pc.operatingExpenses} compare={compare} negative invert variant="total" />

              {/* OPERATING PROFIT */}
              <ResultBand>
                <StatementRow label="OPERATING PROFIT" current={d.operatingProfit} comparison={c?.operatingProfit}
                  change={pc.operatingProfit} compare={compare} variant="result" />
              </ResultBand>

              {/* OTHER INCOME — shown when either range has any */}
              {(d.otherIncome?.lines?.length > 0 || c?.otherIncome?.lines?.length > 0) && (
                <>
                  <SectionHeader label="Other Income" />
                  <AccountLines section={d.otherIncome} compSection={c?.otherIncome} />
                  <StatementRow label="Total Other Income" current={d.otherIncome.total} comparison={c?.otherIncome?.total}
                    change={pc.otherIncome} compare={compare} variant="total" />
                </>
              )}

              {/* NET PROFIT */}
              <ResultBand>
                <StatementRow label="NET PROFIT BEFORE TAX" current={d.netProfitBeforeTax} comparison={c?.netProfitBeforeTax}
                  change={pc.netProfitBeforeTax} compare={compare} variant="result" />
                <StatementRow label={d.corporateTax?.estimate ? 'Corporate Tax (estimate)' : 'Corporate Tax'}
                  current={curTax.value} currentText={curTax.text} comparison={compTax.value} comparisonText={c ? compTax.text : undefined}
                  compare={compare} indent={1} negative showChange={false} note={d.corporateTax?.note} variant="info" />
              </ResultBand>
              <ResultBand tone={Number(d.netProfit) < 0 ? 'loss' : 'profit'}>
                <StatementRow label="NET PROFIT" current={d.netProfit} comparison={c?.netProfit} change={pc.netProfit} compare={compare} variant="net" />
              </ResultBand>

              <Box sx={{ height: 8 }} />
            </>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography sx={{ fontSize: fs.sm, color: 'text.secondary' }}>
                No P&L data available for this period.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* BELOW THE LINE — information only, outside the statement */}
      {!loading && d && (
        <Box sx={{ mt: 1.5, border: 1, borderStyle: 'dashed', borderColor: 'divider', borderRadius: 1, overflowX: 'auto' }}>
          <Box sx={{ minWidth: 520, py: 0.5 }}>
            <Typography sx={{ fontSize: fs.xs, fontWeight: fw.medium, color: 'text.secondary', px: 1.5, pt: 0.5, pb: 0.25 }}>
              Below the Line (not included in P&L)
            </Typography>
            <StatementRow label="Govt Fees & Pass-throughs Disbursed" current={d.belowTheLine.governmentFeesDisbursed}
              comparison={c?.belowTheLine?.governmentFeesDisbursed} compare={compare} indent={1} negative showChange={false} />
            {Number(d.belowTheLine.customerFinesDisbursed || 0) > 0 && (
              <StatementRow label="Customer Fines (pass-through)" current={d.belowTheLine.customerFinesDisbursed}
                comparison={c?.belowTheLine?.customerFinesDisbursed} compare={compare} indent={1} showChange={false} />
            )}
          </Box>
        </Box>
      )}

      {d?.notes?.length > 0 && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          {d.notes.map((n) => <Typography key={n} sx={{ fontSize: fs.xs }}>{n}</Typography>)}
        </Alert>
      )}

      {/* Print styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          nav, header, [class*="Sidebar"], [class*="AppBar"] { display: none !important; }
          main { margin: 0 !important; padding: 0 !important; }
        }
      `}</style>
    </Box>
  );
};

export default ProfitLoss;
